import { formatCurrencyNoDecimals, formatNumber } from '@if/core/utils'
import { StatCard, TrendCaption } from '@if/mui'
import { Box, styled } from '@mui/material'
import { CheckCircle2, DollarSign, FileText, Landmark } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { computeTrendPercent, type MetricTotals } from '../statistics-hooks'

interface StatisticsSummaryCardsProps {
  currentTotals: MetricTotals
  previousTotals: MetricTotals
}

const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('sm')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
  [theme.breakpoints.up('lg')]: { gridTemplateColumns: 'repeat(4, 1fr)' },
  '& > .MuiPaper-root': {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}))

export const StatisticsSummaryCards = ({ currentTotals, previousTotals }: StatisticsSummaryCardsProps) => {
  const { t } = useTranslation()

  return (
    <CardsGrid>
      <StatCard
        icon={FileText}
        iconColor="info"
        label={t('merchantStatistics.performance.stats.applicationsSubmitted')}
        value={formatNumber(currentTotals.totalApplications)}
        caption={
          <TrendCaption
            deltaPercent={computeTrendPercent(currentTotals.totalApplications, previousTotals.totalApplications)}
            fallback={null}
          />
        }
      />
      <StatCard
        icon={CheckCircle2}
        iconColor="success"
        label={t('merchantStatistics.performance.stats.approvedApplications')}
        value={formatNumber(currentTotals.totalApprovedApplications)}
        caption={
          <TrendCaption
            deltaPercent={computeTrendPercent(
              currentTotals.totalApprovedApplications,
              previousTotals.totalApprovedApplications,
            )}
            fallback={null}
          />
        }
      />
      <StatCard
        icon={Landmark}
        iconColor="secondary"
        label={t('merchantStatistics.performance.stats.loansCreated')}
        value={formatNumber(currentTotals.totalCreatedLoans)}
        caption={
          <TrendCaption
            deltaPercent={computeTrendPercent(currentTotals.totalCreatedLoans, previousTotals.totalCreatedLoans)}
            fallback={null}
          />
        }
      />
      <StatCard
        icon={DollarSign}
        iconColor="warning"
        label={t('merchantStatistics.performance.stats.totalLoanAmount')}
        value={formatCurrencyNoDecimals(currentTotals.totalGrantedAmount)}
        caption={
          <TrendCaption
            deltaPercent={computeTrendPercent(currentTotals.totalGrantedAmount, previousTotals.totalGrantedAmount)}
            fallback={null}
          />
        }
      />
    </CardsGrid>
  )
}
